import { Canvas, ThreeEvent } from "@react-three/fiber";
import './model-space.css';
import { forwardRef, LegacyRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Model as RenderedModel } from "./model/model";
import { Menu } from "./menu/menu";
import { POI } from "../poi/poi";
import { BoxHelper } from "./box-helper/box-helper";
import { ModelData as ModelDataData, ModelSpaceExposerRef, ModelSpaceRefExposer } from "./model-space-ref-exposer/model-space-ref-exposer";
import { ModelData } from "./model-data/model-data";
import { TransformControls } from "./controls/transform-controls";
import { ModelSpaceOrbitControlsRef, OrbitControls } from "./controls/orbit-controls";
import * as THREE from 'three';
import { ViewportShading, ViewportShadingSelect } from "./viewport-shading-select/viewport-shading-select";
import { WorkInstruction } from "../../types/api/work-instruction/get-work-instructions-success-response";
import { getModelFromModelTreeInModelSpaceByUUID } from "../../utils/get-model-from-model-tree-in-model-space-by-uuid/get-model-from-model-tree-in-model-space-by-uuid";

export const DEFAULT_CAMERA_POSITION = new THREE.Vector3(2, 2, 10);

export type ModelInModelTree = {
    id?: number;
    originalModel?: File | string;
    modifiedModel?: File | string;
    loaded?: boolean;
    uuid: string;
    name: string;
    visible: boolean;
    isRoot: boolean;
    position: THREE.Vector3Tuple;
    rotation: THREE.EulerTuple;
    scale: THREE.Vector3Tuple;
    originalMaterial?: THREE.Material;
    children?: ModelInModelTree[];
};

export type WorkInstructionWithUUIDs = WorkInstruction & {
    POIUUID: string;
    POILineEndpointUUID: string;
};

type ModelSpaceProps = {
    models: ModelInModelTree[];
    onModelChange?: ( uuid: string, data: ModelDataData) => void;
    onDeleteModel?: (uuid: string) => void;
    onAddNewModel?: (file: File) => void;
    selectedModelUUID: string | null;
    onSelectModel: (uuid: string | null) => void;
    isPOIActive?: boolean;
    workInstructions?: WorkInstructionWithUUIDs[];
    onPOIChange?: (
        uuid: string,
        data: {
            POIXPosition?: number;
            POIYPosition?: number;
            POIZPosition?: number;
        }
    ) => void;
    onPOILineEndpointChange?: (
        uuid: string,
        data: {
            POILineEndpointXPosition?: number;
            POILineEndpointYPosition?: number;
            POILineEndpointZPosition?: number;
        }
    ) => void;
    onModelLoaded?: (uuid: string, successfullyLoaded: boolean, children?: ModelInModelTree[]) => void;
    hasMenu?: boolean;
    isDataEditable?: boolean;
    areModelsLoading: boolean;
    viewportShading?: ViewportShading;
    onViewportShadingChange?: (viewportShading: ViewportShading) => void;
};

export type ModelSpaceRef = ModelSpaceExposerRef & ModelSpaceOrbitControlsRef;

export const ModelSpace = forwardRef<ModelSpaceRef, ModelSpaceProps>(
    (
        { models, onModelChange, onDeleteModel, onAddNewModel, selectedModelUUID, onSelectModel, isPOIActive, workInstructions, onPOIChange, onPOILineEndpointChange, onModelLoaded, hasMenu = true, isDataEditable = true, areModelsLoading, viewportShading, onViewportShadingChange }: ModelSpaceProps,
        modelSpaceRef
    ) => {
        useEffect(() => {
            if (isDataEditable) {
                const handleKeydown = (event: KeyboardEvent) => {
                    if (event.key?.toLowerCase() === 'delete' && selectedModelUUID !== null && onDeleteModel) {
                        onSelectModel(null);
                        onDeleteModel(selectedModelUUID);
                    }
                };

                window.addEventListener('keydown', handleKeydown);

                return () => {
                    window.removeEventListener('keydown', handleKeydown);
                };
            }
        }, [selectedModelUUID, onDeleteModel, onSelectModel, isDataEditable]);

        const [selectedModelData, setSelectedModelData] = useState<ModelDataData>();
        const modelDataTimeoutRef = useRef<NodeJS.Timer>();
        const [onlyPositionInModelData, setOnlyPositionInModelData] = useState(true);
        // állítjuk-e a setInterval-ban folyamatosan modelData-t
        const [settingModelData, setSettingModelData] = useState(true);

        useEffect(() => {
            if (isDataEditable) {
                if ((modelSpaceRef as React.MutableRefObject<ModelSpaceRef>).current && selectedModelUUID !== null) {
                    setSelectedModelData((modelSpaceRef as React.MutableRefObject<ModelSpaceRef>).current.getModelData(selectedModelUUID));
                    setOnlyPositionInModelData((modelSpaceRef as React.MutableRefObject<ModelSpaceRef>).current.isPOI(selectedModelUUID));
                }
            }
        }, [modelSpaceRef, selectedModelUUID, isDataEditable]);

        useEffect(() => {
            if (isDataEditable) {
                if (settingModelData && selectedModelUUID !== null && (modelSpaceRef as React.MutableRefObject<ModelSpaceRef>).current) {
                    modelDataTimeoutRef.current = setInterval(() => {
                        setSelectedModelData((modelSpaceRef as React.MutableRefObject<ModelSpaceRef>).current.getModelData(selectedModelUUID));
                    }, 50);

                    return () => {
                        if (modelDataTimeoutRef.current) clearInterval(modelDataTimeoutRef.current);
                    };
                }
            }
        }, [selectedModelUUID, modelSpaceRef, settingModelData, isDataEditable]);

        const modelSpaceExposerRef = useRef<ModelSpaceExposerRef>();
        const orbitControlsRef = useRef<ModelSpaceOrbitControlsRef>();
        useImperativeHandle(modelSpaceRef, () => ({
            getModifiedModel: (uuid: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.getModifiedModel(uuid);
            },
            getModel: (uuid: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.getModel(uuid);
            },
            deleteByUUID: (uuid: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.deleteByUUID(uuid);

                return false;
            },
            renameModel: (uuid: string, name: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.renameModel(uuid, name);
            },
            getModelData: (uuid: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.getModelData(uuid);
            },
            isPOI: (uuid: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.isPOI(uuid);
                
                return false;
            },
            setModelData: (uuid: string, data: ModelDataData) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.setModelData(uuid, data);
            },
            changeModelvisibility: (uuid: string, visible: boolean) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.changeModelvisibility(uuid, visible);
            },
            getUUIDsOfAModelAndItsChildren: (uuid: string) => {
                if (modelSpaceExposerRef.current) return modelSpaceExposerRef.current.getUUIDsOfAModelAndItsChildren(uuid);

                return new Set<string>();
            },
            setModelMaterial: (uuid: string, material: THREE.Material) => {
                if (modelSpaceExposerRef.current) modelSpaceExposerRef.current.setModelMaterial(uuid, material);
            },
            setModelCenterToOrbitControlsTarget: (uuid: string) => {
                if (orbitControlsRef.current) orbitControlsRef.current.setModelCenterToOrbitControlsTarget(uuid);
            },
            zoomToModel: (uuid: string) => {
                if (orbitControlsRef.current) orbitControlsRef.current.zoomToModel(uuid);
            },
            setCameraPosition: (position: THREE.Vector3) => {
                if (orbitControlsRef.current) orbitControlsRef.current.setCameraPosition(position);
            }
        }), []);

        const createModel = (object: ModelInModelTree) => {
            const handleClick = (e: ThreeEvent<MouseEvent>) => {
                e.stopPropagation();

                if (e.object) {
                    setTimeout(() => {
                        /*if (e.object.userData.isPivotGroup) {
                            onSelectModel(e.object.uuid);
                            return;
                        }

                        if (typeof e.object.userData.isPivotGroup !== 'undefined' && e.object.userData.isPivotGroup === false) {
                            if (e.object.userData.pivotGroupUUID) onSelectModel(e.object.userData.pivotGroupUUID);
                            else onSelectModel(e.object.uuid);

                            return;
                        }*/

                        onSelectModel(e.object.uuid);
                    }, 250);
                }
            };

            const handleDoubleClick = (e: ThreeEvent<MouseEvent>) => {
                e.stopPropagation();

                if (e.object) {
                    setTimeout(() => {
                        onSelectModel(e.object.uuid);
                        if (orbitControlsRef.current) orbitControlsRef.current.zoomToModel(e.object.uuid);
                    }, 250);
                }
            };

            const handlePointerMissed = () => {
                setTimeout(() => {
                    onSelectModel(null);
                }, 150);
            };

            const handleModelLoaded = (uuid: string, successfullyLoaded: boolean, children?: ModelInModelTree[]) => {
                if (onModelLoaded) onModelLoaded(uuid, successfullyLoaded, children);
            };

            return (
                <RenderedModel
                    key={object.uuid}
                    uuid={object.uuid}
                    visible={object.visible}
                    name={object.name}
                    position={object.position}
                    rotation={object.rotation}
                    scale={object.scale}
                    active={selectedModelUUID === object.uuid}
                    model={object.modifiedModel}
                    onClick={handleClick}
                    onDoubleClick={handleDoubleClick}
                    onPointerMissed={handlePointerMissed}
                    onModelLoaded={handleModelLoaded}
                />
            );
        };

        const handleModelChange = (
            uuid: string,
            xPosition: number,
            yPosition: number,
            zPosition: number,
            xRotation: number,
            yRotation: number,
            zRotation: number,
            xScale: number,
            yScale: number,
            zScale: number
        ) => {
            if (onModelChange) onModelChange(uuid, { xPosition, yPosition, zPosition, xRotation, yRotation, zRotation, xScale, yScale, zScale });
        };

        const handleAddNewModel = (file: File) => {
            if (onAddNewModel) onAddNewModel(file);
        };

        const createPOI = (workInstruction: WorkInstructionWithUUIDs) => {
            const handlePOIClick = () => {
                setTimeout(() => {
                    onSelectModel(workInstruction.POIUUID);
                }, 250);
            };

            const handlePOILineEndpointClick = () => {
                setTimeout(() => {
                    onSelectModel(workInstruction.POILineEndpointUUID);
                }, 250);
            };

            const handlePointerMissed = () => {
                setTimeout(() => {
                    onSelectModel(null);
                }, 150);
            };
            
            return (
                <POI
                    key={workInstruction.id}
                    id={workInstruction.id}
                    POIUUID={workInstruction.POIUUID}
                    POILineEndpointUUID={workInstruction.POILineEndpointUUID}
                    workInstruction={workInstruction}
                    onPOIClick={handlePOIClick}
                    onPOILineEndpointClick={handlePOILineEndpointClick}
                    onPointerMissed={handlePointerMissed}
                    numberOfAllPOI={workInstructions?.length}
                />
            );
        };

        const handlePOIChange = (
            uuid: string,
            data: {
                POIXPosition?: number;
                POIYPosition?: number;
                POIZPosition?: number;
            }
        ) => {
            if (onPOIChange) onPOIChange(uuid, data);
        };

        const handlePOILineEndpointChange = (
            uuid: string,
            data: {
                POILineEndpointXPosition?: number;
                POILineEndpointYPosition?: number;
                POILineEndpointZPosition?: number;
            }
        ) => {
            if (onPOILineEndpointChange) onPOILineEndpointChange(uuid, data);
        };

        const handleModeDataChange = (data: ModelDataData) => {
            if (selectedModelUUID === null) return;
    
            setSelectedModelData(prevData => ({ ...prevData, ...data }));

            // modell változás figyelése
            const model = getModelFromModelTreeInModelSpaceByUUID(models, selectedModelUUID);
            if (model) {
                if (onModelChange) onModelChange(selectedModelUUID, { ...data });
                return;
            }
            
            // POI változás figyelése
            if (workInstructions) {
                for (const workInstruction of workInstructions) {
                    if (selectedModelUUID === workInstruction.POIUUID) {
                        if (onPOIChange) onPOIChange(selectedModelUUID, {
                            POIXPosition: data.xPosition,
                            POIYPosition: data.yPosition,
                            POIZPosition: data.zPosition
                        });
                        return;
                    } else if (selectedModelUUID === workInstruction.POILineEndpointUUID) {
                        
                            if (onPOILineEndpointChange) onPOILineEndpointChange(selectedModelUUID, {
                                POILineEndpointXPosition: data.xPosition,
                                POILineEndpointYPosition: data.yPosition,
                                POILineEndpointZPosition: data.zPosition
                            });
                            return;

                    }
                }
            }
        };

        return (
            <div className="model-space-container">

                {
                    areModelsLoading && (
                        <div className="loader">
                            <div className='spinner'>
                                <div className='spinner-border'></div>
                            </div>
                        </div>
                    )
                }
                
                { hasMenu && <Menu onAddNewModel={handleAddNewModel} /> }

                <div className="position-relative">

                    {
                        viewportShading && (
                            <div className="viewport-shading-select-container">
                                <ViewportShadingSelect
                                    viewportShading={viewportShading}
                                    onViewportShadingChange={
                                        (viewportShading) => {
                                            if (onViewportShadingChange) onViewportShadingChange(viewportShading);
                                        }
                                    }
                                />
                            </div>
                        )
                    }

                    <Canvas
                        className="model-space-canvas"
                        camera={
                            {
                                position: DEFAULT_CAMERA_POSITION,
                                near: 0.01,
                                far: 1000,
                                fov: 50
                            }
                        }
                    >

                        {
                            // modellek
                            models.map((model) => createModel(model))
                        }

                        {
                            // POI-k
                            isPOIActive && workInstructions && workInstructions.map(workInstruction => createPOI(workInstruction))
                        }
                        
                        <BoxHelper selectedUUID={selectedModelUUID} />
                        
                        <ambientLight />
                        <directionalLight position={new THREE.Vector3(0, 1000, 0)} />
                        <directionalLight position={new THREE.Vector3(0, -1000, 0)} />
                        <directionalLight position={new THREE.Vector3(1000, 0, 0)} />
                        <directionalLight position={new THREE.Vector3(-1000, 0, 0)} />
                        <directionalLight position={new THREE.Vector3(0, 0, 1000)} />
                        <directionalLight position={new THREE.Vector3(0, 0, -1000)} />

                        <gridHelper />
                        <color attach='background' args={['#ffffffff']} />
                        <OrbitControls
                            selectedModelUUID={selectedModelUUID}
                            ref={orbitControlsRef as LegacyRef<ModelSpaceOrbitControlsRef>}
                        />
                        {
                            isDataEditable && (
                                <TransformControls
                                    selectedUUID={selectedModelUUID}
                                    onModelChange={handleModelChange}
                                    onPOIChange={handlePOIChange}
                                    onPOILineEndpointChange={handlePOILineEndpointChange}
                                    skipSettingModelData={!settingModelData}
                                />
                            )
                        }
                        <ModelSpaceRefExposer ref={modelSpaceExposerRef as LegacyRef<ModelSpaceExposerRef>} />

                    </Canvas>

                </div>
                
                
                {
                        isDataEditable && (
                            <ModelData
                                onlyPosition={onlyPositionInModelData}
                                visible={selectedModelUUID !== null}
                                data={selectedModelData}
                                onChange={handleModeDataChange}
                                onInputFocus={() => setSettingModelData(false)}
                                onInputFocusLose={() => setSettingModelData(true)}
                            />
                        )
                    }
            </div>
        );
    }
);